import { useEffect, useRef, useState } from 'react';
import { IonContent, IonModal, IonPage } from '@ionic/react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/useAuth';
import { getDocument, updateDocumentTitle, moveDocumentToPublication } from '@/lib/api/documents';
import { getMyPublications } from '@/lib/api/publications';
import { getDraftVersion, publishVersion, getVersionsByDocument, getPublishedVersion } from '@/lib/api/versions';
import { createAnnotation, getAnnotationsByVersion } from '@/lib/api/annotations';
import type { DisplayAnnotation } from '@/lib/api/annotations';
import { Logo } from '@/components/ui/Logo';
import { ReaderCanvas } from '@/components/reader/ReaderCanvas';
import { MarginNoteRail } from '@/components/reader/MarginNoteRail';
import { AnnotationPanel } from '@/components/annotations/AnnotationPanel';
import type { AnnotationFilter } from '@/components/annotations/AnnotationPanel';
import { AnnotationCompose } from '@/components/annotations/AnnotationCompose';
import { SelectionToolbar } from '@/components/selection/SelectionToolbar';
import { useSelectionCapture } from '@/components/selection/useSelectionCapture';
import { resolveSelector } from '@/lib/annotations/resolve';
import type { Selector } from '@/lib/annotations/selectors';
import { useRealtimeAnnotations } from '@/hooks/useRealtimeAnnotations';

export default function DocumentPage() {
  const { id } = useParams<{ id: string }>();
  const { status, user } = useAuth();
  const history = useHistory();
  const queryClient = useQueryClient();

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [publishError, setPublishError] = useState<string | null>(null);
  const [movingPub, setMovingPub] = useState(false);
  const [composing, setComposing] = useState(false);
  const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null);
  const [annFilter, setAnnFilter] = useState<AnnotationFilter>('all');
  const [mobileAnnOpen, setMobileAnnOpen] = useState(false);
  const [orphanedIds, setOrphanedIds] = useState<ReadonlySet<string>>(new Set());

  const contentRef = useRef<HTMLDivElement>(null);
  const { captured, clear } = useSelectionCapture(contentRef);

  const { data: doc, isLoading: docLoading, isError: docError } = useQuery({
    queryKey: ['document', id],
    queryFn: () => getDocument(id),
  });

  const { data: draft, isLoading: draftLoading } = useQuery({
    queryKey: ['draft', id],
    queryFn: () => getDraftVersion(id),
    enabled: !!doc,
  });

  const { data: versions = [] } = useQuery({
    queryKey: ['versions', id],
    queryFn: () => getVersionsByDocument(id),
    enabled: !!doc,
  });

  const { data: publishedContent } = useQuery({
    queryKey: ['published-content', id],
    queryFn: () => getPublishedVersion(id),
    enabled: !!doc,
  });

  const { data: annotationData } = useQuery({
    queryKey: ['annotations', publishedContent?.id],
    queryFn: () => getAnnotationsByVersion(publishedContent!.id),
    enabled: !!publishedContent,
  });

  const { data: myPublications = [] } = useQuery({
    queryKey: ['my-publications'],
    queryFn: getMyPublications,
    enabled: status === 'authenticated',
  });

  const moveMutation = useMutation({
    mutationFn: (publicationId: string) => moveDocumentToPublication(id, publicationId),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['documents', doc?.publication_id] });
      queryClient.invalidateQueries({ queryKey: ['documents', updated.publication_id] });
      setMovingPub(false);
    },
  });

  const annotations: DisplayAnnotation[] = annotationData?.display ?? [];
  const orphanedAnnotations = annotations.filter(a => orphanedIds.has(a.id));

  useRealtimeAnnotations(publishedContent?.id);

  // Apply highlight marks after content + annotations are rendered.
  // Tracks which annotation IDs can't be resolved (orphaned) for the UI.
  useEffect(() => {
    const container = contentRef.current;

    // Remove any existing marks from a prior render.
    container?.querySelectorAll('mark[data-ann-id]').forEach(m => {
      const parent = m.parentNode;
      if (!parent) return;
      while (m.firstChild) parent.insertBefore(m.firstChild, m);
      parent.removeChild(m);
      parent.normalize();
    });

    if (!container || annotations.length === 0) {
      setOrphanedIds(new Set());
      return;
    }

    const nextOrphaned = new Set<string>();

    for (const ann of annotations) {
      const range = resolveSelector(container, ann.selector as Selector);
      if (!range) { nextOrphaned.add(ann.id); continue; }
      try {
        const mark = document.createElement('mark');
        mark.dataset.annId = ann.id;
        mark.style.backgroundColor = ann.color + '30';
        mark.style.borderRadius = '2px';
        mark.style.cursor = 'pointer';
        mark.title = ann.bodyMd.slice(0, 80);
        mark.addEventListener('click', () => setActiveAnnotationId(ann.id));
        range.surroundContents(mark);
      } catch {
        // Cross-element selection — skip highlight; annotation still shows in rail.
        nextOrphaned.add(ann.id);
      }
    }

    setOrphanedIds(prev => {
      if (prev.size === nextOrphaned.size && [...nextOrphaned].every(id => prev.has(id))) return prev;
      return nextOrphaned;
    });
  // Re-run when the displayed content or annotation list changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedContent?.id, annotations.length]);

  // On mobile (< lg), clicking a highlight should open the annotation panel.
  useEffect(() => {
    if (activeAnnotationId && window.innerWidth < 1024) setMobileAnnOpen(true);
  }, [activeAnnotationId]);

  useEffect(() => {
    if (doc) setTitleValue(doc.title);
  }, [doc]);

  useEffect(() => {
    const prev = document.title;
    if (doc) document.title = `${doc.title} — Open iPub`;
    return () => { document.title = prev; };
  }, [doc]);

  const saveTitleMutation = useMutation({
    mutationFn: (title: string) => updateDocumentTitle(id, title),
    onSuccess: updated => {
      queryClient.setQueryData(['document', id], updated);
      queryClient.invalidateQueries({ queryKey: ['documents', doc?.publication_id] });
      setEditingTitle(false);
    },
  });

  const publishMutation = useMutation({
    mutationFn: (versionId: string) => publishVersion(versionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['draft', id] });
      queryClient.invalidateQueries({ queryKey: ['versions', id] });
      queryClient.invalidateQueries({ queryKey: ['documents', doc?.publication_id] });
      queryClient.invalidateQueries({ queryKey: ['published-content', id] });
      setPublishError(null);
    },
    onError: (err: unknown) => {
      const pgErr = err as { message?: string };
      setPublishError(pgErr?.message ?? 'Publish failed.');
    },
  });

  const annotationMutation = useMutation({
    mutationFn: ({ selector, bodyMd, visibility }: {
      selector: Selector;
      bodyMd: string;
      visibility: 'public' | 'private' | 'author_only';
    }) => createAnnotation(publishedContent!.id, selector, bodyMd, visibility),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['annotations', publishedContent?.id] });
      setComposing(false);
      clear();
    },
  });

  function handleAnnotationSave({ note, isPublic }: { note: string; isPublic: boolean }) {
    if (!captured || !publishedContent) return;
    annotationMutation.mutate({
      selector: captured.selector,
      bodyMd: note,
      visibility: isPublic ? 'public' : 'private',
    });
  }

  if (status === 'unauthenticated') return <Redirect to="/auth" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  const isOwner = status === 'authenticated' && !!user;
  const publishedVersion = versions.find(v => v.published_at);
  const canAnnotate = status === 'authenticated' && !!publishedContent;

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-4xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.goBack()}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
            <div className="ml-auto flex items-center gap-2">
              {annotations.length > 0 && (
                <button
                  onClick={() => setMobileAnnOpen(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 lg:hidden"
                  aria-label="View annotations"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  {annotations.length}
                </button>
              )}
              {draft && isOwner && (
                <>
                  <button
                    onClick={() => history.push(`/edit/${id}`)}
                    className="rounded-lg border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Open editor
                  </button>
                  <button
                    onClick={() => publishMutation.mutate(draft.id)}
                    disabled={publishMutation.isPending}
                    className="rounded-lg bg-[#2f3e46] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
                  >
                    {publishMutation.isPending ? 'Publishing…' : publishedVersion ? 'Republish' : 'Publish'}
                  </button>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-5 py-8 pb-20">
          {(docLoading || draftLoading) && (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
            </div>
          )}

          {docError && (
            <p className="py-20 text-center text-gray-500">Document not found.</p>
          )}

          {doc && (
            <>
              <div className="mb-6">
                {editingTitle ? (
                  <form
                    onSubmit={e => { e.preventDefault(); saveTitleMutation.mutate(titleValue); }}
                    className="flex gap-2"
                  >
                    <input
                      type="text"
                      value={titleValue}
                      onChange={e => setTitleValue(e.target.value)}
                      maxLength={200}
                      autoFocus
                      className="flex-1 rounded-lg border border-gray-200 px-3.5 py-2 font-serif text-2xl font-bold outline-none focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                    />
                    <button
                      type="submit"
                      disabled={saveTitleMutation.isPending || !titleValue.trim()}
                      className="rounded-lg bg-[#2f3e46] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTitleValue(doc.title); setEditingTitle(false); }}
                      className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700"
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <div className="group flex items-start gap-3">
                    <h1 className="font-serif text-2xl font-bold text-gray-900">{doc.title}</h1>
                    {isOwner && (
                      <button
                        onClick={() => setEditingTitle(true)}
                        className="mt-1 rounded p-1 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100 hover:text-gray-500"
                        aria-label="Edit title"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-400">
                  {publishedVersion ? (
                    <span className="rounded-full bg-green-50 px-2 py-0.5 text-green-700">
                      Published v{publishedVersion.version_number}
                    </span>
                  ) : (
                    <span className="rounded-full bg-yellow-50 px-2 py-0.5 text-yellow-700">Draft</span>
                  )}
                  {draft && !draft.published_at && publishedVersion && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">
                      New draft (v{draft.version_number})
                    </span>
                  )}
                  {/* Move to publication — inline select, owner only */}
                  {isOwner && myPublications.length > 1 && (
                    movingPub ? (
                      <div className="flex items-center gap-1.5">
                        <select
                          autoFocus
                          defaultValue={doc.publication_id}
                          disabled={moveMutation.isPending}
                          onChange={e => moveMutation.mutate(e.target.value)}
                          className="rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-700 outline-none focus:border-[#1a56a0]"
                        >
                          {myPublications.map(p => (
                            <option key={p.id} value={p.id}>{p.title}</option>
                          ))}
                        </select>
                        <button onClick={() => setMovingPub(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setMovingPub(true)}
                        className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-500 hover:bg-gray-200"
                      >
                        Move to…
                      </button>
                    )
                  )}
                </div>
              </div>

              {publishError && (
                <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{publishError}</p>
              )}

              {publishedContent && publishedContent.content_jsonb && (
                <div className="lg:flex lg:gap-6">
                  {/* Article body — selection capture attached here */}
                  <div
                    ref={contentRef}
                    className="relative min-w-0 flex-1"
                    style={{ WebkitTouchCallout: 'none' } as React.CSSProperties}
                  >
                    <ReaderCanvas content={publishedContent.content_jsonb as Record<string, unknown>} />
                    {canAnnotate && !captured && (
                      <p className="mt-4 text-center text-xs text-gray-400">
                        Select any text to annotate
                      </p>
                    )}
                  </div>

                  {/* Desktop margin rail */}
                  <MarginNoteRail
                    annotations={annotations}
                    orphaned={orphanedAnnotations}
                    activeId={activeAnnotationId}
                    onAnnotationClick={ann => setActiveAnnotationId(ann.id === activeAnnotationId ? null : ann.id)}
                  />
                </div>
              )}

              {draft && isOwner && !publishedContent && (
                <button
                  onClick={() => history.push(`/edit/${id}`)}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 py-10 text-sm font-medium text-gray-400 transition-colors hover:border-[#2f3e46] hover:text-[#2f3e46]"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Open in editor
                </button>
              )}

              {versions.length > 0 && (
                <div className="mt-10 border-t border-gray-100 pt-6">
                  <h2 className="mb-3 text-xs font-extrabold uppercase tracking-widest text-gray-400">
                    Version history
                  </h2>
                  <div className="flex flex-col gap-1">
                    {versions.map(v => (
                      <div key={v.id} className="flex items-center gap-3 py-1.5 text-sm">
                        <span className="font-medium text-gray-700">v{v.version_number}</span>
                        {v.published_at ? (
                          <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">Published</span>
                        ) : (
                          <span className="rounded-full bg-yellow-50 px-2 py-0.5 text-xs text-yellow-700">Draft</span>
                        )}
                        <span className="text-gray-400">
                          {new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </main>

        {/* Selection toolbar */}
        {canAnnotate && captured && !composing && (
          <SelectionToolbar
            selection={{ text: captured.text, rect: captured.rect }}
            onAnnotate={() => setComposing(true)}
            onDismiss={clear}
          />
        )}

        {/* Annotation compose modal */}
        {canAnnotate && captured && composing && (
          <AnnotationCompose
            selection={{ text: captured.text, rect: captured.rect }}
            onSave={handleAnnotationSave}
            onCancel={() => { setComposing(false); clear(); }}
          />
        )}

        {/* Mobile: bottom-sheet annotation panel */}
        <IonModal
          isOpen={mobileAnnOpen}
          onDidDismiss={() => setMobileAnnOpen(false)}
          initialBreakpoint={0.5}
          breakpoints={[0, 0.5, 0.9]}
        >
          <AnnotationPanel
            annotations={annotations}
            orphaned={orphanedAnnotations}
            onClose={() => setMobileAnnOpen(false)}
            onAnnotationClick={ann => {
              setActiveAnnotationId(ann.id);
              setMobileAnnOpen(false);
            }}
            activeId={activeAnnotationId}
            filter={annFilter}
            onFilterChange={setAnnFilter}
          />
        </IonModal>
      </IonContent>
    </IonPage>
  );
}
